import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';
import { AnalyticsDashboardResponse, DashboardView, DashboardViewPayload, Group, User, Workflow } from '../types';
import { useArea } from '../context/AreaContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import './AnalyticsDashboard.css';

const toDateInput = (value: Date): string => value.toISOString().slice(0, 10);

const formatHours = (seconds: number): string => {
  if (!seconds) return '0h 0min';
  const sign = seconds < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(seconds) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${sign}${hours}h ${minutes}min`;
};

const defaultWidgetOrder = ['kpis', 'line', 'pie', 'heatmap', 'priority', 'group', 'category', 'stepTime', 'summary'];

const widgetTitle: Record<string, string> = {
  kpis: 'KPIs e comparativos',
  line: 'Volumetria por período',
  pie: 'Distribuição por status',
  heatmap: 'Heatmap diário',
  priority: 'Distribuição por prioridade',
  group: 'Ranking por grupo',
  category: 'Categorias / campo customizado',
  stepTime: 'Tempo por etapa',
  summary: 'Tabela resumida',
};

const AnalyticsDashboard: React.FC = () => {
  const { currentArea } = useArea();
  const [loading, setLoading] = useState(true);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState<AnalyticsDashboardResponse | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [views, setViews] = useState<DashboardView[]>([]);

  const [dateFrom, setDateFrom] = useState(toDateInput(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)));
  const [dateTo, setDateTo] = useState(toDateInput(new Date()));
  const [workflowId, setWorkflowId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [customFieldId, setCustomFieldId] = useState('');
  const [customFieldValue, setCustomFieldValue] = useState('');
  const [groupByCustomFieldId, setGroupByCustomFieldId] = useState('');
  const [widgetOrder, setWidgetOrder] = useState<string[]>(defaultWidgetOrder);
  const [dragWidget, setDragWidget] = useState('');
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewAsDefault, setViewAsDefault] = useState(false);
  const [defaultViewApplied, setDefaultViewApplied] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const areaType = currentArea === 'purchases' ? 'purchases' : 'tickets';
      const [groupsRes, workflowsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/groups`, { params: { limit: 1000, include_members: false } }),
        axios.get(`${API_URL}/api/workflows/published`, { params: { area_type: areaType } }),
        axios.get(`${API_URL}/api/users`),
      ]);
      setGroups(groupsRes.data.filter((item: Group) => item.is_active));
      setWorkflows(workflowsRes.data);
      setUsers(usersRes.data.filter((item: User) => item.is_active));
    } catch (fetchError) {
      console.error('Erro ao carregar filtros do dashboard analítico:', fetchError);
    }
  }, [currentArea]);

  const fetchViews = useCallback(async () => {
    try {
      const response = await axios.get<DashboardView[]>(`${API_URL}/api/analytics/views`, {
        params: { area_type: currentArea },
      });
      setViews(response.data);
    } catch (fetchError) {
      console.error('Erro ao carregar vistas:', fetchError);
    }
  }, [currentArea]);

  const fetchAnalytics = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (silent) setIsRefreshingData(true);
    else setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {
        area_type: currentArea,
        date_from: dateFrom,
        date_to: dateTo,
      };
      if (workflowId) params.workflow_id = workflowId;
      if (groupId) params.group_id = groupId;
      if (categoryId) params.category_id = categoryId;
      if (priority) params.priority = priority;
      if (status) params.status = status;
      if (assignedTo) params.assigned_to = assignedTo;
      if (customFieldId) params.custom_field_id = customFieldId;
      if (customFieldValue) params.custom_field_value = customFieldValue;
      if (groupByCustomFieldId) params.group_by_custom_field_id = groupByCustomFieldId;

      const response = await axios.get<AnalyticsDashboardResponse>(`${API_URL}/api/analytics/dashboard`, { params });
      setAnalytics(response.data);
    } catch (fetchError: any) {
      const detail = fetchError?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível carregar os dados analíticos.');
    } finally {
      if (silent) setIsRefreshingData(false);
      else setLoading(false);
    }
  }, [
    assignedTo,
    categoryId,
    currentArea,
    customFieldId,
    customFieldValue,
    dateFrom,
    dateTo,
    groupByCustomFieldId,
    groupId,
    priority,
    status,
    workflowId,
  ]);

  useEffect(() => {
    fetchFilterOptions();
    fetchViews();
    const raw = localStorage.getItem(`analytics_widget_order_${currentArea}`);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setWidgetOrder(parsed.map((item) => String(item)));
        }
      } catch {
        setWidgetOrder(defaultWidgetOrder);
      }
    } else {
      setWidgetOrder(defaultWidgetOrder);
    }
  }, [currentArea, fetchFilterOptions, fetchViews]);

  useEffect(() => {
    setDefaultViewApplied(false);
    setSelectedViewId('');
  }, [currentArea]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    localStorage.setItem(`analytics_widget_order_${currentArea}`, JSON.stringify(widgetOrder));
  }, [currentArea, widgetOrder]);

  const comparisonRows = useMemo(() => {
    if (!analytics) return [];
    return [
      { label: 'Total', data: analytics.comparison.total_items, isTime: false },
      { label: 'Tempo médio (h)', data: analytics.comparison.avg_total_time_seconds, isTime: true },
      { label: 'SLA dentro (%)', data: analytics.comparison.sla_within_percentage, isTime: false },
      { label: 'Backlog aberto', data: analytics.comparison.backlog_open_count, isTime: false },
    ];
  }, [analytics]);

  const lineData = useMemo(() => {
    if (!analytics) return [];
    return analytics.volume_by_period.map((item, index) => ({
      date: item.date,
      current: item.count,
      previous: analytics.previous_volume_by_period[index]?.count ?? 0,
    }));
  }, [analytics]);

  const maxLine = useMemo(() => {
    if (!lineData.length) return 1;
    return Math.max(...lineData.map((item) => Math.max(item.current, item.previous)), 1);
  }, [lineData]);

  const topSummary = useMemo(() => {
    if (!analytics) return [];
    return [
      ...analytics.by_group.slice(0, 5).map((item) => ({ type: 'Grupo', label: item.label, value: item.value })),
      ...analytics.by_workflow.slice(0, 5).map((item) => ({ type: 'Workflow', label: item.label, value: item.value })),
      ...analytics.by_category.slice(0, 5).map((item) => ({ type: 'Categoria', label: item.label, value: item.value })),
      ...analytics.by_custom_field.slice(0, 5).map((item) => ({ type: 'Custom', label: item.label, value: item.value })),
    ].sort((a, b) => b.value - a.value);
  }, [analytics]);

  const totalStatus = useMemo(() => analytics?.by_status.reduce((acc, item) => acc + item.value, 0) || 0, [analytics]);
  const customFieldOptions = analytics?.available_custom_fields || [];

  const heatmap = useMemo(() => {
    if (!analytics) return [];
    const maxValue = Math.max(...analytics.volume_by_period.map((item) => item.count), 1);
    return analytics.volume_by_period.map((item, idx) => ({
      date: item.date,
      count: item.count,
      row: idx % 7,
      col: Math.floor(idx / 7),
      intensity: item.count / maxValue,
    }));
  }, [analytics]);

  const maxHeatCol = useMemo(() => (heatmap.length ? Math.max(...heatmap.map((item) => item.col)) + 1 : 1), [heatmap]);

  const doDrillDown = (query: Record<string, string>) => {
    if (!analytics) return;
    const search = new URLSearchParams();
    const baseFilters: Record<string, string> = {};
    if (dateFrom) baseFilters.created_from = dateFrom;
    if (dateTo) baseFilters.created_to = dateTo;
    if (workflowId) baseFilters.workflow_id = workflowId;
    if (groupId) baseFilters.assigned_group_id = groupId;
    if (priority) baseFilters.priority = priority;
    if (status) baseFilters.status = status;
    if (assignedTo) baseFilters.assigned_to = assignedTo;
    if (customFieldId) baseFilters.custom_field_id = customFieldId;
    if (customFieldValue) baseFilters.custom_field_value = customFieldValue;
    if (categoryId) baseFilters.custom_field_id = categoryId;

    Object.entries({ ...baseFilters, ...query }).forEach(([key, value]) => search.set(key, value));
    const drilldownUrl = `${analytics.drilldown_base_path}?${search.toString()}`;
    const link = document.createElement('a');
    link.href = drilldownUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const downloadSvg = (svgId: string, fileName: string) => {
    const element = document.getElementById(svgId);
    if (!element) return;
    const serialized = new XMLSerializer().serializeToString(element);
    const blob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    if (!analytics) return;
    const lines = ['tipo,chave,label,valor'];
    const append = (type: string, list: { key: string; label: string; value: number }[]) => {
      list.forEach((item) => lines.push(`${type},${item.key},"${item.label.replace(/"/g, '""')}",${item.value}`));
    };
    append('status', analytics.by_status);
    append('priority', analytics.by_priority);
    append('group', analytics.by_group);
    append('workflow', analytics.by_workflow);
    append('category', analytics.by_category);
    append('custom', analytics.by_custom_field);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `analytics_${currentArea}_${dateFrom}_${dateTo}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportXlsx = () => {
    if (!analytics) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        { metrica: 'total', valor: analytics.kpis.total_items },
        { metrica: 'tempo_medio_segundos', valor: analytics.kpis.avg_total_time_seconds },
        { metrica: 'sla_dentro_percentual', valor: analytics.kpis.sla_within_percentage },
        { metrica: 'backlog_aberto', valor: analytics.kpis.backlog_open_count },
      ]),
      'kpis',
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analytics.volume_by_period), 'volumetria');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analytics.by_status), 'status');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analytics.by_group), 'grupos');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analytics.by_category), 'categorias');
    XLSX.writeFile(wb, `analytics_${currentArea}_${dateFrom}_${dateTo}.xlsx`);
  };

  const clearFilters = () => {
    setDateFrom(toDateInput(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)));
    setDateTo(toDateInput(new Date()));
    setWorkflowId('');
    setGroupId('');
    setCategoryId('');
    setPriority('');
    setStatus('');
    setAssignedTo('');
    setCustomFieldId('');
    setCustomFieldValue('');
    setGroupByCustomFieldId('');
  };

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) return;
    const payload: DashboardViewPayload = {
      name,
      area_type: currentArea,
      is_default: viewAsDefault,
      filters_json: {
        dateFrom,
        dateTo,
        workflowId,
        groupId,
        categoryId,
        priority,
        status,
        assignedTo,
        customFieldId,
        customFieldValue,
        groupByCustomFieldId,
      },
      layout_json: {
        widgetOrder,
      },
    };
    await axios.post(`${API_URL}/api/analytics/views`, payload);
    setShowSaveModal(false);
    setViewName('');
    setViewAsDefault(false);
    fetchViews();
  };

  const applyView = useCallback((viewId: string) => {
    setSelectedViewId(viewId);
    const selected = views.find((item) => String(item.id) === viewId);
    if (!selected) return;
    const filters = selected.filters_json || {};
    setDateFrom(String(filters.dateFrom || dateFrom));
    setDateTo(String(filters.dateTo || dateTo));
    setWorkflowId(String(filters.workflowId || ''));
    setGroupId(String(filters.groupId || ''));
    setCategoryId(String(filters.categoryId || ''));
    setPriority(String(filters.priority || ''));
    setStatus(String(filters.status || ''));
    setAssignedTo(String(filters.assignedTo || ''));
    setCustomFieldId(String(filters.customFieldId || ''));
    setCustomFieldValue(String(filters.customFieldValue || ''));
    setGroupByCustomFieldId(String(filters.groupByCustomFieldId || ''));
    const layout = selected.layout_json || {};
    if (Array.isArray(layout.widgetOrder)) {
      setWidgetOrder(layout.widgetOrder.map((item) => String(item)));
    }
  }, [views, dateFrom, dateTo]);

  useEffect(() => {
    if (defaultViewApplied || selectedViewId || views.length === 0) return;
    const defaultView = views.find((item) => item.is_default);
    if (defaultView) {
      applyView(String(defaultView.id));
    }
    setDefaultViewApplied(true);
  }, [applyView, defaultViewApplied, selectedViewId, views]);

  const updateView = async () => {
    if (!selectedViewId) return;
    await axios.put(`${API_URL}/api/analytics/views/${selectedViewId}`, {
      filters_json: {
        dateFrom,
        dateTo,
        workflowId,
        groupId,
        categoryId,
        priority,
        status,
        assignedTo,
        customFieldId,
        customFieldValue,
        groupByCustomFieldId,
      },
      layout_json: { widgetOrder },
    });
    fetchViews();
  };

  const deleteView = async () => {
    if (!selectedViewId) return;
    await axios.delete(`${API_URL}/api/analytics/views/${selectedViewId}`);
    setSelectedViewId('');
    fetchViews();
  };

  const onDropWidget = (target: string) => {
    if (!dragWidget || dragWidget === target) return;
    const current = [...widgetOrder];
    const source = current.indexOf(dragWidget);
    const targetIndex = current.indexOf(target);
    if (source === -1 || targetIndex === -1) return;
    const [removed] = current.splice(source, 1);
    current.splice(targetIndex, 0, removed);
    setWidgetOrder(current);
    setDragWidget('');
  };

  const renderWidget = (id: string) => {
    if (!analytics) return null;
    if (id === 'kpis') {
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            <Row className="g-2">
              <Col md={3}><div className="analytics-metric-box"><small>Total</small><h4>{analytics.kpis.total_items}</h4></div></Col>
              <Col md={3}><div className="analytics-metric-box"><small>Tempo médio</small><h4>{formatHours(analytics.kpis.avg_total_time_seconds)}</h4></div></Col>
              <Col md={3}><div className="analytics-metric-box"><small>SLA dentro</small><h4>{analytics.kpis.sla_within_percentage}%</h4></div></Col>
              <Col md={3}><div className="analytics-metric-box"><small>Backlog</small><h4>{analytics.kpis.backlog_open_count}</h4></div></Col>
            </Row>
            <Table size="sm" className="mt-3">
              <thead><tr><th>Métrica</th><th>Atual</th><th>Anterior</th><th>Delta</th></tr></thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.isTime ? formatHours(row.data.current) : row.data.current}</td>
                    <td>{row.isTime ? formatHours(row.data.previous) : row.data.previous}</td>
                    <td className={row.data.delta >= 0 ? 'text-success' : 'text-danger'}>
                      {row.isTime ? formatHours(row.data.delta) : row.data.delta} ({row.data.delta_percentage}%)
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      );
    }
    if (id === 'line') {
      const width = 760;
      const height = 220;
      const pad = 24;
      const stepX = lineData.length > 1 ? (width - pad * 2) / (lineData.length - 1) : width - pad * 2;
      const y = (value: number) => height - pad - (value / maxLine) * (height - pad * 2);
      const yTicks = 5;
      const currentPath = lineData.map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${pad + idx * stepX} ${y(point.current)}`).join(' ');
      const previousPath = lineData.map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${pad + idx * stepX} ${y(point.previous)}`).join(' ');
      return (
        <Card className="h-100">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>{widgetTitle[id]} (linha)</strong>
            <Button size="sm" variant="outline-secondary" onClick={() => downloadSvg('svg-line', 'volumetria.svg')}>Exportar gráfico</Button>
          </Card.Header>
          <Card.Body>
            <svg id="svg-line" viewBox={`0 0 ${width} ${height}`} className="analytics-svg">
              {Array.from({ length: yTicks + 1 }).map((_, tickIdx) => {
                const value = Math.round((maxLine / yTicks) * tickIdx);
                const tickY = y(value);
                return (
                  <g key={`tick-${tickIdx}`}>
                    <line x1={pad} y1={tickY} x2={width - pad} y2={tickY} stroke="#e9ecef" />
                    <text x={pad - 6} y={tickY + 4} fontSize="10" textAnchor="end" fill="#6c757d">
                      {value}
                    </text>
                  </g>
                );
              })}
              <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#adb5bd" />
              <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#adb5bd" />
              <path d={previousPath} fill="none" stroke="#6c757d" strokeWidth="2" />
              <path d={currentPath} fill="none" stroke="#1d428a" strokeWidth="2.5" />
              {lineData.map((point, idx) => {
                const showLabel = idx === 0 || idx === lineData.length - 1 || idx % 7 === 0;
                return (
                  <g key={point.date}>
                    <circle cx={pad + idx * stepX} cy={y(point.current)} r="3" fill="#1d428a" onClick={() => doDrillDown({ created_from: point.date, created_to: point.date })} style={{ cursor: 'pointer' }} />
                    {showLabel ? (
                      <text x={pad + idx * stepX} y={height - 8} fontSize="9" textAnchor="middle" fill="#6c757d">
                        {point.date.slice(5)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </svg>
          </Card.Body>
        </Card>
      );
    }
    if (id === 'pie') {
      const colors = ['#1d428a', '#6bb63f', '#ffc107', '#dc3545', '#6f42c1', '#198754', '#fd7e14', '#6c757d'];
      let acc = 0;
      const slices = analytics.by_status.map((item) => {
        const start = acc;
        const pct = totalStatus ? item.value / totalStatus : 0;
        acc += pct;
        return { ...item, start, end: acc };
      });
      const arc = (start: number, end: number) => {
        const r = 90;
        const cx = 110;
        const cy = 110;
        const s = 2 * Math.PI * start - Math.PI / 2;
        const e = 2 * Math.PI * end - Math.PI / 2;
        const x1 = cx + r * Math.cos(s);
        const y1 = cy + r * Math.sin(s);
        const x2 = cx + r * Math.cos(e);
        const y2 = cy + r * Math.sin(e);
        const large = end - start > 0.5 ? 1 : 0;
        return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      };
      return (
        <Card className="h-100">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>{widgetTitle[id]} (pizza)</strong>
            <Button size="sm" variant="outline-secondary" onClick={() => downloadSvg('svg-pie', 'status.svg')}>Exportar gráfico</Button>
          </Card.Header>
          <Card.Body className="d-flex gap-3 align-items-center flex-wrap">
            <svg id="svg-pie" width="220" height="220" className="analytics-svg">
              {slices.map((slice, idx) => (
                <path key={slice.key} d={arc(slice.start, slice.end)} fill={colors[idx % colors.length]} onClick={() => doDrillDown({ status: slice.key })} style={{ cursor: 'pointer' }} />
              ))}
            </svg>
            <div>
              {slices.map((slice, idx) => (
                <div key={slice.key} className="analytics-pie-legend-item">
                  <span className="legend-dot" style={{ background: colors[idx % colors.length] }} />
                  <span>{slice.label} ({slice.value})</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      );
    }
    if (id === 'heatmap') {
      const cell = 16;
      const gap = 3;
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            <svg id="svg-heatmap" width={maxHeatCol * (cell + gap) + 12} height={7 * (cell + gap) + 12} className="analytics-svg">
              {heatmap.map((item) => (
                <rect key={item.date} x={6 + item.col * (cell + gap)} y={6 + item.row * (cell + gap)} width={cell} height={cell} rx={3} fill={`rgba(13,110,253,${Math.max(0.12, item.intensity)})`} onClick={() => doDrillDown({ created_from: item.date, created_to: item.date })} style={{ cursor: 'pointer' }}>
                  <title>{item.date}: {item.count}</title>
                </rect>
              ))}
            </svg>
          </Card.Body>
        </Card>
      );
    }
    if (id === 'priority') {
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            {analytics.by_priority.map((item) => (
              <div key={item.key} className="d-flex justify-content-between analytics-inline-row">
                <button className="analytics-link-btn" onClick={() => doDrillDown({ priority: item.key })}>{item.label}</button>
                <strong>{item.value}</strong>
              </div>
            ))}
          </Card.Body>
        </Card>
      );
    }
    if (id === 'group') {
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            {analytics.by_group.slice(0, 8).map((item) => (
              <div key={item.key} className="d-flex justify-content-between analytics-inline-row">
                <button className="analytics-link-btn" onClick={() => doDrillDown({ assigned_group_id: item.key })}>{item.label}</button>
                <strong>{item.value}</strong>
              </div>
            ))}
          </Card.Body>
        </Card>
      );
    }
    if (id === 'category') {
      const list = analytics.by_custom_field.length ? analytics.by_custom_field : analytics.by_category;
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            {list.slice(0, 10).map((item) => (
              <div key={`${id}-${item.key}`} className="d-flex justify-content-between analytics-inline-row">
                <button
                  className="analytics-link-btn"
                  onClick={() => {
                    const query: Record<string, string> = {};
                    if (analytics.by_custom_field.length && groupByCustomFieldId) {
                      query.custom_field_id = groupByCustomFieldId;
                      query.custom_field_value = item.label;
                    } else {
                      if (categoryId) query.custom_field_id = categoryId;
                      query.custom_field_value = item.label;
                    }
                    doDrillDown(query);
                  }}
                >
                  {item.label}
                </button>
                <strong>{item.value}</strong>
              </div>
            ))}
          </Card.Body>
        </Card>
      );
    }
    if (id === 'stepTime') {
      return (
        <Card className="h-100">
          <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
          <Card.Body>
            {analytics.step_time_by_stage.slice(0, 10).map((item) => (
              <div key={item.step_name} className="d-flex justify-content-between analytics-inline-row">
                <span>{item.step_name}</span>
                <span>{formatHours(item.avg_time_seconds)} ({item.total_records})</span>
              </div>
            ))}
          </Card.Body>
        </Card>
      );
    }
    return (
      <Card className="h-100">
        <Card.Header><strong>{widgetTitle[id]}</strong></Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead><tr><th>Tipo</th><th>Item</th><th className="text-end">Qtd</th></tr></thead>
            <tbody>
              {topSummary.map((item, idx) => (
                <tr key={`${item.type}-${item.label}-${idx}`}>
                  <td>{item.type}</td>
                  <td>{item.label}</td>
                  <td className="text-end">{item.value}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const {
    isRefreshing: isAutoRefreshing,
    secondsSinceRefresh,
    refreshNow,
  } = useAutoRefresh({
    refreshFn: () => fetchAnalytics({ silent: true }),
    enabled: true,
    intervalMs: 30000,
  });

  const refreshStatusLabel = useMemo(() => {
    if (isAutoRefreshing || isRefreshingData) return 'Atualizando...';
    if (secondsSinceRefresh == null) return 'Aguardando primeira atualização automática';
    if (secondsSinceRefresh < 60) return `Atualizado há ${secondsSinceRefresh}s`;
    return `Atualizado há ${Math.floor(secondsSinceRefresh / 60)}min`;
  }, [isAutoRefreshing, isRefreshingData, secondsSinceRefresh]);

  return (
    <div className="analytics-dashboard-page">
      <div className="analytics-hero mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="analytics-hero-title">
          <h2 className="mb-1">Relatórios - Dashboard Analítico</h2>
          <p className="text-muted mb-0">
            Visão consolidada de {currentArea === 'purchases' ? 'requisições de compras' : 'tickets'} com filtros globais.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap analytics-toolbar">
          <Button variant="outline-secondary" onClick={() => void refreshNow()} disabled={loading || isAutoRefreshing}>
            Atualizar agora
          </Button>
          <Button variant="outline-primary" onClick={exportCsv}>Exportar CSV</Button>
          <Button variant="outline-primary" onClick={exportXlsx}>Exportar XLSX</Button>
          <Button variant="outline-secondary" onClick={() => setShowSaveModal(true)}>Salvar vista</Button>
          <Button variant="outline-secondary" onClick={updateView} disabled={!selectedViewId}>Atualizar vista</Button>
          <Button variant="outline-danger" onClick={deleteView} disabled={!selectedViewId}>Excluir vista</Button>
        </div>
      </div>
      <div className="analytics-live-meta">
        <span className="analytics-live-dot" />
        <small>{refreshStatusLabel}</small>
      </div>
      </div>

      <Card className="mb-4 analytics-section-card">
        <Card.Header><strong>Filtros Globais</strong></Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}><Form.Group><Form.Label>Vista salva</Form.Label><Form.Select value={selectedViewId} onChange={(e) => applyView(e.target.value)}><option value="">Selecionar...</option>{views.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_default ? ' (padrão)' : ''}</option>)}</Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Data inicial</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Data final</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Workflow</Form.Label><Form.Select value={workflowId} onChange={(e) => setWorkflowId(e.target.value)}><option value="">Todos</option>{workflows.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Grupo</Form.Label><Form.Select value={groupId} onChange={(e) => setGroupId(e.target.value)}><option value="">Todos</option>{groups.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Status</Form.Label><Form.Select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Todos</option><option value="novo">Novo</option><option value="aberto">Aberto</option><option value="em_andamento">Em Andamento</option><option value="resolvido">Resolvido</option><option value="cancelado">Cancelado</option><option value="fechado">Fechado</option></Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Prioridade</Form.Label><Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}><option value="">Todas</option><option value="p1">P1</option><option value="p2">P2</option><option value="p3">P3</option><option value="p4">P4</option></Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Responsável</Form.Label><Form.Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}><option value="">Todos</option>{users.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Categoria (ID)</Form.Label><Form.Control type="number" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Filtro por campo customizado</Form.Label><Form.Select value={customFieldId} onChange={(e) => setCustomFieldId(e.target.value)}><option value="">Nenhum</option>{customFieldOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</Form.Select></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Valor do campo customizado</Form.Label><Form.Control value={customFieldValue} onChange={(e) => setCustomFieldValue(e.target.value)} placeholder="texto/valor" /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Agrupar por campo customizado</Form.Label><Form.Select value={groupByCustomFieldId} onChange={(e) => setGroupByCustomFieldId(e.target.value)}><option value="">Não</option>{customFieldOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</Form.Select></Form.Group></Col>
          </Row>
          <div className="d-flex gap-2 mt-3 flex-wrap">
            <Button onClick={() => fetchAnalytics()} disabled={loading}>Aplicar filtros</Button>
            <Button variant="outline-secondary" onClick={clearFilters} disabled={loading}>Limpar filtros</Button>
            {(isAutoRefreshing || isRefreshingData) && <small className="text-muted align-self-center">Sincronizando dados analíticos...</small>}
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
      ) : (
        <Row className="g-3">
          {widgetOrder.map((widgetId) => (
            <Col key={widgetId} md={widgetId === 'kpis' ? 12 : 6} lg={widgetId === 'kpis' ? 12 : 6}>
              <div
                className="analytics-widget-shell"
                draggable
                onDragStart={() => setDragWidget(widgetId)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropWidget(widgetId)}
              >
                {renderWidget(widgetId)}
              </div>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} className="analytics-themed-modal">
        <Modal.Header closeButton>
          <Modal.Title>Salvar vista do dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome da vista</Form.Label>
            <Form.Control value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="Ex.: SLA Compras - 30 dias" />
          </Form.Group>
          <Form.Check type="checkbox" checked={viewAsDefault} onChange={(e) => setViewAsDefault(e.target.checked)} label="Definir como padrão" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>Cancelar</Button>
          <Button onClick={saveView}>Salvar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AnalyticsDashboard;
